import React, { useEffect, useState } from "react";
import { FaEdit, FaPlus, FaTrash } from "react-icons/fa";
import {
  ConfirmDialog,
  FormField,
  LoadingSpinner,
  PrimaryButton,
  SecondaryButton,
  formatCurrency,
} from "../UI/DesignStandards";
import { notify } from "../UI/DesignStandards/Notifications/notify";
import { sanitizeOperationError } from "../../utility/errorSanitization";
import { supabase } from "../../supabaseClient";
import {
  defaultProductFields,
  productLabel,
  suggestedPayouts,
} from "../../utility/brackets/constants";
import {
  listBracketProducts,
  saveBracketProduct,
  softDeleteBracketProduct,
} from "../../utility/brackets/api";

const DetailRow = ({ label, value }) => (
  <div>
    <div className="text-xs font-medium text-brand-muted mb-1">{label}</div>
    <div className="text-sm text-brand-charcoal">{value}</div>
  </div>
);

const fieldsFromProduct = (product) => ({
  name: product.name || "",
  productKind: product.productKind || "traditional",
  scoringMode: product.scoringMode || "scratch",
  gameOrder: product.gameOrder || [1, 2, 3],
  treeSize: 8,
  entryAmount: Number(product.entryAmount),
  houseCut: Number(product.houseCut),
  firstPlaceAmount: Number(product.firstPlaceAmount),
  secondPlaceAmount: Number(product.secondPlaceAmount),
  tieRule: product.tieRule || "usbc_wc",
  sourceEventID: product.sourceEventID || "",
});

const ProductCard = ({
  tournamentId,
  product,
  events,
  onSaved,
  isNew,
  onCancelNew,
}) => {
  const [fields, setFields] = useState(
    isNew || !product?.id ? defaultProductFields() : fieldsFromProduct(product)
  );
  const [isEditing, setIsEditing] = useState(Boolean(isNew));
  const [saving, setSaving] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);

  const display = isEditing
    ? fields
    : product?.id
      ? fieldsFromProduct(product)
      : fields;

  const setField = (key, value) => {
    setFields((prev) => {
      const next = { ...prev, [key]: value };
      if (key === "entryAmount" || key === "houseCut") {
        const suggested = suggestedPayouts(
          key === "entryAmount" ? value : next.entryAmount,
          key === "houseCut" ? value : next.houseCut
        );
        next.firstPlaceAmount = suggested.firstPlaceAmount;
        next.secondPlaceAmount = suggested.secondPlaceAmount;
      }
      if (key === "scoringMode" && !isNew && product?.name) {
        return next;
      }
      if (key === "scoringMode" && (isNew || !product?.name)) {
        const amount = Number(next.entryAmount) || 5;
        next.name =
          value === "handicap" ? `Handicap $${amount}` : `Scratch $${amount}`;
      }
      return next;
    });
  };

  const handleSave = async (event) => {
    event.preventDefault();
    if (!fields.name.trim()) {
      notify.error("Name the bracket.");
      return;
    }
    setSaving(true);
    try {
      await saveBracketProduct(
        tournamentId,
        {
          ...fields,
          sourceEventID: fields.sourceEventID || null,
        },
        isNew ? null : product.id
      );
      setIsEditing(false);
      onSaved();
    } catch (error) {
      notify.error(sanitizeOperationError.save(error, "brackets"));
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    try {
      await softDeleteBracketProduct(product.id);
      setConfirmDelete(false);
      onSaved();
    } catch (error) {
      notify.error(sanitizeOperationError.delete(error, "brackets"));
    }
  };

  return (
    <div className="card-inset p-4 space-y-4">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h3 className="heading-display text-lg">
            {isEditing
              ? isNew || !product?.id
                ? "New bracket"
                : "Edit bracket"
              : productLabel(product)}
          </h3>
        </div>
        {!isEditing && product?.id ? (
          <div className="flex gap-2">
            <SecondaryButton
              type="button"
              icon={FaEdit}
              onClick={() => {
                setFields(fieldsFromProduct(product));
                setIsEditing(true);
              }}
            >
              Edit
            </SecondaryButton>
          </div>
        ) : null}
      </div>

      {isEditing ? (
        <form onSubmit={handleSave} className="space-y-4">
          <FormField
            label="Name"
            value={fields.name}
            onChange={(event) => setField("name", event.target.value)}
            required
          />
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <FormField
              label="Scratch or handicap"
              type="select"
              value={fields.scoringMode}
              onChange={(event) => setField("scoringMode", event.target.value)}
            >
              <option value="scratch">Scratch</option>
              <option value="handicap">Handicap</option>
            </FormField>
            <FormField
              label="Scores from"
              type="select"
              value={fields.sourceEventID}
              onChange={(event) => setField("sourceEventID", event.target.value)}
            >
              <option value="">Any event on the squad</option>
              {events.map((eventRow) => (
                <option key={eventRow.id} value={eventRow.id}>
                  {eventRow.eventName}
                </option>
              ))}
            </FormField>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
            <FormField
              label="Entry"
              type="number"
              min="0"
              step="0.01"
              value={fields.entryAmount}
              onChange={(event) => setField("entryAmount", event.target.value)}
              required
            />
            <FormField
              label="House cut"
              type="number"
              min="0"
              step="0.01"
              value={fields.houseCut}
              onChange={(event) => setField("houseCut", event.target.value)}
            />
            <FormField
              label="1st"
              type="number"
              min="0"
              step="0.01"
              value={fields.firstPlaceAmount}
              onChange={(event) =>
                setField("firstPlaceAmount", event.target.value)
              }
              required
            />
            <FormField
              label="2nd"
              type="number"
              min="0"
              step="0.01"
              value={fields.secondPlaceAmount}
              onChange={(event) =>
                setField("secondPlaceAmount", event.target.value)
              }
              required
            />
          </div>
          <p className="text-sm text-brand-muted">
            Ties: Game 1 both advance. Game 2 broken by Game 3. Game 3 chops 1st
            and 2nd.
          </p>
          <div className="flex flex-wrap gap-2">
            <PrimaryButton type="submit" loading={saving}>
              Save
            </PrimaryButton>
            <SecondaryButton
              type="button"
              onClick={() => {
                if (isNew) {
                  onCancelNew();
                  return;
                }
                setFields(fieldsFromProduct(product));
                setIsEditing(false);
              }}
            >
              Cancel
            </SecondaryButton>
            {product?.id ? (
              <SecondaryButton
                type="button"
                icon={FaTrash}
                onClick={() => setConfirmDelete(true)}
              >
                Remove
              </SecondaryButton>
            ) : null}
          </div>
        </form>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
          <DetailRow
            label="Scoring"
            value={display.scoringMode === "handicap" ? "Handicap" : "Scratch"}
          />
          <DetailRow label="Entry" value={formatCurrency(display.entryAmount)} />
          <DetailRow label="House cut" value={formatCurrency(display.houseCut)} />
          <DetailRow
            label="1st / 2nd"
            value={`${formatCurrency(display.firstPlaceAmount)} / ${formatCurrency(
              display.secondPlaceAmount
            )}`}
          />
        </div>
      )}

      <ConfirmDialog
        isOpen={confirmDelete}
        onClose={() => setConfirmDelete(false)}
        onConfirm={handleDelete}
        title="Remove this bracket?"
        message="It will no longer be sold. Existing tickets on shuffled squads stay until you unlock them."
        confirmText="Remove"
      />
    </div>
  );
};

const BracketProductsSetup = ({ tournamentId, onBracketsChange }) => {
  const [products, setProducts] = useState([]);
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [adding, setAdding] = useState(false);

  const load = async () => {
    if (!tournamentId) {
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const [nextProducts, eventResult] = await Promise.all([
        listBracketProducts(tournamentId),
        supabase
          .from("tournamentEvents")
          .select("id, eventName, isPrimary, isDeleted")
          .eq("tournamentID", tournamentId)
          .eq("isPrimary", true)
          .eq("isDeleted", false),
      ]);
      if (eventResult.error) throw eventResult.error;
      setProducts(nextProducts);
      setEvents(eventResult.data || []);
      setAdding(false);
    } catch (error) {
      notify.error(sanitizeOperationError.read(error, "brackets"));
    } finally {
      setLoading(false);
    }
  };

  const handleSaved = async () => {
    await load();
    onBracketsChange?.();
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tournamentId]);

  if (loading) {
    return (
      <div className="card-board p-6">
        <h2 className="heading-display text-xl mb-6">Brackets</h2>
        <div className="card-inset p-4 flex justify-center">
          <LoadingSpinner />
        </div>
      </div>
    );
  }

  return (
    <div className="card-board p-6">
      <div className="flex items-start justify-between gap-4 mb-6">
        <div>
          <h2 className="heading-display text-xl">Brackets</h2>
          <p className="text-sm text-brand-muted mt-1">
            Eight bowlers buy tickets into a knockout. Winners advance. First
            and second get paid from those tickets. Add each scratch or handicap
            bracket this tournament sells.
          </p>
        </div>
        <SecondaryButton
          type="button"
          icon={FaPlus}
          onClick={() => setAdding(true)}
          disabled={adding}
          className="!px-3 !py-2 text-sm flex-shrink-0"
        >
          Add bracket
        </SecondaryButton>
      </div>

      <div className="space-y-4">
        {adding ? (
          <ProductCard
            isNew
            tournamentId={tournamentId}
            product={{}}
            events={events}
            onSaved={handleSaved}
            onCancelNew={() => setAdding(false)}
          />
        ) : null}

        {products.length === 0 && !adding ? (
          <div className="card-inset p-4">
            <p className="text-sm text-brand-charcoal">
              No brackets. Add a scratch or handicap bracket.
            </p>
          </div>
        ) : null}

        {products.map((product) => (
          <ProductCard
            key={product.id}
            tournamentId={tournamentId}
            product={product}
            events={events}
            onSaved={handleSaved}
          />
        ))}
      </div>
    </div>
  );
};

export default BracketProductsSetup;
